import React, { useEffect, useState } from 'react';

import { usePathname } from 'expo-router';
import StoreConfirmationModal from 'features/store/components/store-confirmation-modal';
import { useIntl } from 'react-intl';
import { Dimensions, GestureResponderEvent } from 'react-native';

import ActionButton from 'components/action-button';
import { CheckoutErrorsModal } from 'components/checkout-errors-modal';
import { LogUserErrorSeverity, useLogUserErrorMessage } from 'hooks/log-user-error-message';
import { useRumPageView } from 'hooks/rum/use-rum-page-view';
import { useCart } from 'hooks/use-cart';
import useDialogModal from 'hooks/use-dialog-modal';
import { useGetPaymentErrors } from 'hooks/use-get-payment-errors';
import useIsSignUpAfterCart from 'hooks/use-is-sign-up-after-cart';
import useTraceLoading from 'hooks/use-trace-loading';
import { CheckOutStickyFooter } from 'pages/cart/payment/sticky-footer';
import { useCartContext } from 'state/cart';
import { RunningExperiments, useExperiments } from 'state/experiments';
import { selectors, useAppSelector } from 'state/global-state';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { OrderStatus, useOrderContext } from 'state/order';
import { useUIContext } from 'state/ui';
import { routes } from 'utils/routing';
import { isDelivery } from 'utils/service-mode';

import { useIsVehicleDescriptionEnabled } from '../hooks/use-is-vehicle-description-enabled';

import SignUpToContinueActionLink from './sign-up-to-continue-action-link';
import { CartPriceTooLow, SignUpToContinueDisclaimer } from './styled';
import { IPaymentProps } from './types';
import { logCartContinue } from './utils';

const Payment: React.FC<React.PropsWithChildren<IPaymentProps>> = ({
  disabled = false,
  serverOrder,
  pricedAndAvailable,
  areDeliveryDetailsValid,
  offerIsValid,
  orderStatus,
  scrollViewRef,
  phoneNumberInputRef,
  setDeliveryDetailsErrors,
  shouldSkipPriceOrder,
}) => {
  useRumPageView('payment', 'Payment');
  const { logExposure } = useExperiments();
  const pathname = usePathname();

  // note that this check is required to ensure that the experiment is not logged on cart as well
  // this component is used in cart for some reason, TODO: please lets change it
  useEffect(() => {
    if (pathname === routes.cartPayment) {
      logExposure(RunningExperiments.PaymentCheckoutRedesign);
    }
  }, [logExposure, pathname]);

  const { formatCurrencyForLocale } = useUIContext();
  const {
    saveVehicleDescription,
    serviceMode,
    vehicleDescription: { value: vehicleDescriptionValue, hasError: vehicleDescriptionHasError },
  } = useCartContext();
  const { calculateCartTotal, calculateCartTotalWithDiscount } = useCart();
  const {
    alertOrderDeliveryMinimum,
    deliveryAddress,
    orderPhoneNumber,
    serviceMode: orderServiceMode,
    selectServiceMode,
  } = useOrderContext();

  const [isCheckoutErrorsModalOpen, setIsCheckoutErrorsModalOpen] = useState(false);

  const { formatMessage } = useIntl();

  const checkoutDeliveryPriceMinimum = useFlag(LaunchDarklyFlag.OVERRIDE_CHECKOUT_DELIVERY_MINIMUM);
  const enableRequireAddress2 = useFlag(
    LaunchDarklyFlag.ENABLE_DELIVERY_DETAILS_FORM_REQUIRE_ADDRESS_2
  );
  const evaluatingIncentives = useAppSelector(selectors.loyalty.selectOffersLoading);

  const isSignUpAfterCart = useIsSignUpAfterCart();
  const isVehicleDescriptionEnabled = useIsVehicleDescriptionEnabled();

  const isDeliveryOrder = isDelivery(serviceMode);

  const checkoutPriceMinimum = isDeliveryOrder ? checkoutDeliveryPriceMinimum : 0;

  const cartSubTotal =
    isSignUpAfterCart || shouldSkipPriceOrder
      ? calculateCartTotal()
      : Number(serverOrder?.cart?.subTotalCents);
  const cartPriceTooLow = cartSubTotal < checkoutPriceMinimum;

  const { getErrorMessages } = useGetPaymentErrors({ cartPriceTooLow });

  const [StoreConfirmationDialog, , , dismissStoreConfirmationDialog] = useDialogModal({
    showCancel: true,
    Component: StoreConfirmationModal,
    modalAppearanceEventMessage: 'Confirmation Modal: Store Location',
  });

  const { shouldConfirmStoreOnCheckout } = useUIContext();

  const { isCartTotalNegative = false } = calculateCartTotalWithDiscount();

  const isPriceRequested = !serverOrder || orderStatus === OrderStatus.PRICE_REQUESTED;
  const isOrderPriceLoading = !isCartTotalNegative && isPriceRequested && !shouldSkipPriceOrder;
  const isLoading = isOrderPriceLoading || evaluatingIncentives;

  const handleContinuePress = (e: GestureResponderEvent) => {
    // if loading do nothing
    if (isLoading) {
      e?.preventDefault();
      e?.stopPropagation();
      return;
    }
    logCartContinue();

    // Cart Service Mode and Order Service Mode need to be the same at this moment
    // to Prevent wrong reporting to LD and Amplitude
    if (serviceMode !== orderServiceMode) {
      selectServiceMode(serviceMode);
    }

    if (isVehicleDescriptionEnabled) {
      if (!vehicleDescriptionHasError && vehicleDescriptionValue) {
        saveVehicleDescription(vehicleDescriptionValue);
      } else {
        scrollViewRef?.current?.scrollToEnd({ animated: true });
      }
    }
  };

  const onStoreConfirmation = () => {
    dismissStoreConfirmationDialog();
    shouldConfirmStoreOnCheckout(true);
  };

  useEffect(alertOrderDeliveryMinimum, [alertOrderDeliveryMinimum]);

  const paymentErrorMessages = getErrorMessages({
    areDeliveryDetailsWithError: !areDeliveryDetailsValid,
    invalidAddressLine2: enableRequireAddress2 && isDeliveryOrder && !deliveryAddress?.addressLine2,
    invalidVehicleDescription:
      isVehicleDescriptionEnabled && (vehicleDescriptionHasError || !vehicleDescriptionValue),
    invalidOfferError: !offerIsValid,
    pricedAndAvailableError: !pricedAndAvailable,
  });

  const hasErrors = !!paymentErrorMessages.length;

  const handleErrorContinueClick = () => {
    if (!areDeliveryDetailsValid && !orderPhoneNumber) {
      // Delivery Orders, set phone number error
      // when phone number input value has not changed and is empty.
      setDeliveryDetailsErrors({
        phoneNumber: formatMessage({ id: 'phoneNumberRequiredError' }),
      });
    }
    logCartContinue();
    setIsCheckoutErrorsModalOpen(true);
  };

  const scrolledToFocusedInput = () => {
    phoneNumberInputRef?.current?.measure(
      (x: number, y: number, width: number, height: number, pageX: number, pageY: number) => {
        const { height: windowHeight } = Dimensions.get('window');
        const scrollToYValue = pageY > 0 ? pageY : windowHeight - Math.abs(pageY);
        scrollViewRef?.current?.scrollTo({
          y: scrollToYValue,
          animated: true,
        });

        // Workaround to open keyboard on input focus.
        setTimeout(() => {
          phoneNumberInputRef?.current?.blur();
          phoneNumberInputRef?.current?.focus();
        }, 50);
      }
    );
  };

  useTraceLoading({
    name: 'Continue',
    isLoading,
  });

  const ContinueButton = () =>
    hasErrors ? (
      <ActionButton
        onPress={handleErrorContinueClick}
        fullWidth
        isLoading={isLoading}
        noLogOnLongLoading
        disabled={disabled || !offerIsValid || cartPriceTooLow || shouldSkipPriceOrder || isLoading}
      >
        {formatMessage({ id: 'continue' })}
      </ActionButton>
    ) : (
      <ActionButton
        onPress={handleContinuePress}
        to={routes.cartPayment}
        fullWidth
        isLoading={isLoading}
        noLogOnLongLoading
        disabled={disabled || cartPriceTooLow}
      >
        {formatMessage({ id: 'continue' })}
      </ActionButton>
    );

  const cartPriceTooLowMessage = formatMessage(
    {
      id: 'deliveryMinumumNotReached',
    },
    {
      min: formatCurrencyForLocale(checkoutDeliveryPriceMinimum),
    }
  );

  // TODO FIX technically message visibility should be tied to isLoading (it flashes "minimum not reached" on page load every time)
  // but trying to not change any page logic with these error message additions to reduce risk
  // bug here: https://rbictg.atlassian.net/browse/BKPE-2611
  useLogUserErrorMessage({
    message: cartPriceTooLowMessage,
    logWhenTrue: cartPriceTooLow && !isLoading,
    severity: LogUserErrorSeverity.Recoverable,
  });

  const CTAButton = isSignUpAfterCart ? (
    <>
      <SignUpToContinueDisclaimer>
        {formatMessage({ id: 'signUpToContinueDisclaimer' })}
      </SignUpToContinueDisclaimer>
      <SignUpToContinueActionLink />
    </>
  ) : (
    <ContinueButton />
  );
  const priceTooLowCmp = cartPriceTooLow && !isLoading && (
    <CartPriceTooLow>{cartPriceTooLowMessage}</CartPriceTooLow>
  );

  return (
    <>
      <CheckOutStickyFooter
        serverOrder={serverOrder}
        serviceMode={serviceMode}
        priceTooLowCmp={priceTooLowCmp}
        CTAButton={CTAButton}
        shouldSkipPriceOrder={shouldSkipPriceOrder}
        orderStatus={orderStatus}
      />

      {/* modals */}
      <StoreConfirmationDialog
        onConfirm={onStoreConfirmation}
        onDismiss={dismissStoreConfirmationDialog}
      />
      {isCheckoutErrorsModalOpen && (
        <CheckoutErrorsModal
          checkoutErrors={paymentErrorMessages}
          closeModal={() => {
            setIsCheckoutErrorsModalOpen(false);
            scrolledToFocusedInput();
          }}
        />
      )}
    </>
  );
};

export default Payment;
